import { Badge, Box, Button, Flex, Text, VStack, useTheme } from "@chakra-ui/react";
import { isTaskRetrying, isTaskRetryExhausted, isThrottleError, taskService } from "api/ai/ai.service";
import { RightSidebarHeader } from "components/Layout/RightSidebarWidget";
import { formatDate } from "utils";
import AIProgressWidget from "./AIProgressWidget";
import { useRightSidebar } from "context/RightSidebarContext";
import { Hash, Tag, CircleNotch, User, CalendarBlank, CheckCircle, ListBullets, WarningDiamond } from "@phosphor-icons/react";
import { TASK_TYPE_LABELS } from "./taskTypeLabels";

const STATUS_COLOR = {
  done: "green",
  running: "blue",
  queued: "orange",
  failed: "red",
  cancelled: "gray",
};

/**
 * @param {Object} p
 * @param {AITask} p.task
 */
export default function AITaskDetailsRSidebar({ task }) {
  const { doClose } = useRightSidebar();
  const { colors } = useTheme();

  const handleCancel = () => {
    taskService.cancel(task.id);
  };

  return (
    <Box id="AITaskDetailsPanel" display="flex" flexDirection="column" h="full" mx={4}>
      <RightSidebarHeader>{TASK_TYPE_LABELS[task.task_type] || task.task_type}</RightSidebarHeader>

      <Flex direction="column" gap={6} flex={1} mt={4} overflowY="auto" minH={0}
        sx={{
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
          "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
          scrollbarWidth: "thin",
          scrollbarColor: "transparent transparent",
          "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
        }}
      >
        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <Tag size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Label</Text>
          </Flex>
          <Text fontSize="body.s" fontWeight="bold" color="darkTeal">{task.name || "-"}</Text>
        </Flex>

        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <CircleNotch size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Status</Text>
          </Flex>
          <Badge
            px={2} py={1} borderRadius="md" fontSize="body.xxs"
            colorScheme={isTaskRetrying(task) ? "orange" : (STATUS_COLOR[task.status] || "gray")}
            width="fit-content"
          >
            {isTaskRetrying(task)
              ? `Retrying (${task.retry_count}/${task.max_retries ?? "?"})`
              : task.status}
          </Badge>
          {isTaskRetrying(task) && (
            <Text fontSize="xs" color="gray.500">
              Rate-limited by the AI provider. Will retry automatically.
            </Text>
          )}
        </Flex>

        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <ListBullets size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Progress</Text>
          </Flex>
          <Box sx={{ "& > div": { alignItems: "flex-start" }, "& .chakra-progress": { width: "100%" } }}>
            <AIProgressWidget task={task} refetchInterval={5000} />
          </Box>
        </Flex>

        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <User size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">User</Text>
          </Flex>
          <Text fontSize="body.s" color="gray.700">{task.user_id || "-"}</Text>
        </Flex>

        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <CalendarBlank size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Created</Text>
          </Flex>
          <Badge px={2} py={1} borderRadius="md" fontSize="body.xxs" colorScheme="gray" width="fit-content">
            {formatDate(task.created_date) || "-"}
          </Badge>
        </Flex>

        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <CheckCircle size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Completed</Text>
          </Flex>
          <Badge
            px={2} py={1} borderRadius="md" fontSize="body.xxs"
            colorScheme={task.completed_date ? "green" : "gray"}
            width="fit-content"
          >
            {task.completed_date ? formatDate(task.completed_date) : "Not complete"}
          </Badge>
        </Flex>

        <Flex direction="column" gap={2}>
          <Flex gap={2} align="center">
            <Hash size={18} color={colors.gray[400]} />
            <Text variant="statusTitle">Task ID</Text>
          </Flex>
          <Text fontSize="xs" color="gray.500" fontFamily="mono" wordBreak="break-all">{task.id}</Text>
        </Flex>

        {task.inputs && (
          <Flex direction="column" gap={2}>
            <Text variant="statusTitle">Inputs</Text>
            <Text fontSize="xs" color="gray.500" wordBreak="break-all">{JSON.stringify(task.inputs)}</Text>
          </Flex>
        )}

        {task.outputs && (
          <Flex direction="column" gap={2}>
            <Text variant="statusTitle">Outputs</Text>
            <Text fontSize="xs" color="gray.500" wordBreak="break-all">{Object.keys(task.outputs).join(", ")}</Text>
          </Flex>
        )}

        {task.error && (
          <Flex direction="column" gap={2}>
            <Flex gap={2} align="center">
              <WarningDiamond size={18} color={colors.red[400]} />
              <Text variant="statusTitle" color="red.600">Error</Text>
            </Flex>
            {isTaskRetryExhausted(task) && (
              <Text fontSize="sm" color="red.600" fontWeight="semibold">
                {isThrottleError(task.error)
                  ? `Gave up after ${task.retry_count} retries — Bedrock throttled.`
                  : `Failed after ${task.retry_count} retries.`}
              </Text>
            )}
            <Text fontSize="sm" color="red.500">{task.error}</Text>
          </Flex>
        )}
      </Flex>

      <Box pt={4} flexShrink={0} borderTop="1px solid" borderColor="gray.200">
        <VStack spacing={2}>
          {task.status === "running" || task.status === "queued" ? (
            <Button w="full" colorScheme="red" variant="outline" onClick={handleCancel}>
              Cancel Task
            </Button>
          ) : null}
          <Button w="full" variant="secondary" onClick={doClose}>
            Close
          </Button>
        </VStack>
      </Box>
    </Box>
  );
}
